import { tipTapContentByParagraph } from '@lessonup/pin-renderer';
import {
  filterAnimatedComponents,
  isTextPinComponent,
  PinComponentId,
  PinComponentWithAnimations,
  TeacherPin,
  TextPinComponent,
  toJSONContent,
} from '@lessonup/pins-shared';
import { isEqual } from 'lodash';
import { AnimationStepRef } from '../playerStore.types';
import { Direction } from '../reducers/reducers.types';

/** Returns an ordered list of all animation steps in a pin. */
export function getAnimationSteps(pin: TeacherPin): AnimationStepRef[] {
  const pinComponents = filterAnimatedComponents(pin.pinComponents);
  return Array.from(expandAnimations(pinComponents));
}

/**
 * Expands a list of components with animations into animation steps.
 *
 * Most components have a single animation step, but text components with
 * paragraph granularity can have multiple steps.
 */
function* expandAnimations(components: PinComponentWithAnimations[]): Generator<AnimationStepRef> {
  for (const component of components) {
    if (isTextPinComponent(component) && component.settings.animations.entry.granularity === 'paragraph') {
      yield* expandParagraphAnimation(component);
    } else {
      yield { componentId: component.id, step: 0 };
    }
  }
}

function* expandParagraphAnimation(component: TextPinComponent): Generator<AnimationStepRef> {
  const tipTapContent = component.settings.tipTapContent;
  const jsonContent = typeof tipTapContent === 'string' ? toJSONContent(tipTapContent) : tipTapContent;

  if (!jsonContent) {
    yield { componentId: component.id, step: 0 };
    return;
  }

  yield* Array.from(tipTapContentByParagraph(jsonContent)).map((_, index) => ({
    componentId: component.id,
    step: index,
  }));
}

/**
 * Computes the pin components that are hidden because their animations have not
 * yet been played.
 *
 * @param animationSteps All animation steps for the current pin, sorted by order.
 * @param lastPlayedAnimationStep The animation step that was last played, if any.
 */
export function computeHiddenAnimatedComponents(
  animationSteps: AnimationStepRef[],
  lastPlayedAnimationStep: AnimationStepRef | null
): Set<PinComponentId> {
  // Find the index of the current animation, or -1 if no animation has been
  // played yet.
  // -1 <= currentIndex < animations.length
  const currentIndex = animationSteps.findIndex((step) => isEqual(step, lastPlayedAnimationStep));

  const hidden = new Set<PinComponentId>();

  // Hide all components from animations yet to come...
  for (let i = currentIndex + 1; i < animationSteps.length; i++) {
    hidden.add(animationSteps[i].componentId);
  }

  // ...except if they also appear in animations we've already seen.
  for (let i = 0; i <= currentIndex; i++) {
    hidden.delete(animationSteps[i].componentId);
  }

  return hidden;
}

/**
 * Advances the animation state by one step in the given direction.
 *
 * @returns The new "last played" animation, or null for the initial state
 * (before any animations have been played), or false if we've reached the end
 * and we cannot move in the requested direction.
 */
export function advanceAnimationState(
  animationSteps: AnimationStepRef[],
  lastPlayedAnimationStep: AnimationStepRef | null,
  direction: Direction
): AnimationStepRef | null | false {
  // Find the index of the current animation, or -1 if no animation has been
  // played yet.
  // -1 <= currentIndex < animations.length
  const currentIndex = animationSteps.findIndex((step) => isEqual(step, lastPlayedAnimationStep));

  // -2 <= nextIndex < animations.length + 1
  const nextIndex = currentIndex + direction;

  if (nextIndex === -2) {
    // We're trying to move before the beginning of the pin.
    return false;
  }

  if (nextIndex === -1) {
    // We're moving to the beginning of the pin, so we return the empty
    // animation state.
    return null;
  }

  if (nextIndex < animationSteps.length) {
    // We're moving to the next animation, and there still is one.
    return animationSteps[nextIndex];
  }

  // We've reached the end of the pin, and there are no more animations.
  return false;
}
